import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Divider, { dividerClasses } from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { paperClasses } from '@mui/material/Paper';
import { listClasses } from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuButton from './MenuButton';
import { useAuth } from '../../contexts/AuthContext';
import { useColorMode } from '../../contexts/ColorModeContext';
import { api } from '../../services/api';

const MenuItem = styled(MuiMenuItem)({
  margin: '2px 0',
});

interface OptionsMenuProps {
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
}

export default function OptionsMenu({ anchorEl: externalAnchorEl, onClose: externalOnClose }: OptionsMenuProps = {}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { mode, setMode } = useColorMode();
  const [internalAnchorEl, setInternalAnchorEl] = React.useState<null | HTMLElement>(null);

  // Change password dialog
  const [changePwOpen, setChangePwOpen] = React.useState(false);
  const [currentPw, setCurrentPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirmPw, setConfirmPw] = React.useState('');
  const [pwError, setPwError] = React.useState('');
  const [pwSuccess, setPwSuccess] = React.useState(false);
  const [pwLoading, setPwLoading] = React.useState(false);

  const openChangePassword = () => {
    setCurrentPw(''); setNewPw(''); setConfirmPw('');
    setPwError(''); setPwSuccess(false);
    setChangePwOpen(true);
    handleClose();
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (newPw !== confirmPw) { setPwError('New passwords do not match'); return; }
    if (newPw.length < 6) { setPwError('New password must be at least 6 characters'); return; }
    setPwLoading(true);
    try {
      await api.changePassword(currentPw, newPw);
      setPwSuccess(true);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  // Use external anchor if provided, otherwise use internal
  const anchorEl = externalAnchorEl !== undefined ? externalAnchorEl : internalAnchorEl;
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setInternalAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalAnchorEl(null);
    }
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  return (
    <React.Fragment>
      {externalAnchorEl === undefined && (
        <MenuButton
          aria-label="Open menu"
          onClick={handleClick}
          sx={{ borderColor: 'transparent' }}
        >
          <MoreVertRoundedIcon />
        </MenuButton>
      )}
      <Menu
        anchorEl={anchorEl}
        id="menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          [`& .${listClasses.root}`]: {
            padding: '4px',
          },
          [`& .${paperClasses.root}`]: {
            padding: 0,
          },
          [`& .${dividerClasses.root}`]: {
            margin: '4px -4px',
          },
        }}
      >
        <MenuItem onClick={openChangePassword}>
          <ListItemIcon><LockOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Change Password</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          selected={mode === 'light'}
          onClick={() => { setMode('light'); handleClose(); }}
        >
          <ListItemIcon><LightModeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Light</ListItemText>
        </MenuItem>
        <MenuItem
          selected={mode === 'dark'}
          onClick={() => { setMode('dark'); handleClose(); }}
        >
          <ListItemIcon><DarkModeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Dark</ListItemText>
        </MenuItem>
        <MenuItem
          selected={mode === 'system'}
          onClick={() => { setMode('system'); handleClose(); }}
        >
          <ListItemIcon><SettingsBrightnessIcon fontSize="small" /></ListItemIcon>
          <ListItemText>System</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            [`& .${listItemIconClasses.root}`]: {
              ml: 'auto',
              minWidth: 0,
            },
          }}
        >
          <ListItemText>Logout</ListItemText>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <Dialog open={changePwOpen} onClose={() => setChangePwOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {pwSuccess ? (
              <Alert severity="success">Password changed successfully.</Alert>
            ) : (
              <>
                {pwError && <Alert severity="error">{pwError}</Alert>}
                <TextField
                  label="Current Password"
                  type="password"
                  fullWidth
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  disabled={pwLoading}
                  autoComplete="current-password"
                />
                <TextField
                  label="New Password"
                  type="password"
                  fullWidth
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  disabled={pwLoading}
                  helperText="At least 6 characters"
                  autoComplete="new-password"
                />
                <TextField
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  disabled={pwLoading}
                  autoComplete="new-password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePwOpen(false)} disabled={pwLoading}>
            {pwSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!pwSuccess && (
            <Button
              variant="contained"
              onClick={handleChangePassword}
              disabled={pwLoading || !currentPw || !newPw || !confirmPw}
            >
              {pwLoading ? 'Saving…' : 'Change Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
